/**
 * Permission utility functions
 *
 * This module provides utility functions for working with module permissions.
 */

import type {ModulePermission} from './routeUtils'

/**
 * Infers the role category (Platform, Agency, Company) from module permissions.
 *
 * This is used when a custom role doesn't have category information from the
 * role definitions API, but we need to determine the layout variant.
 *
 * @param permissions - Array of module permissions
 * @returns The inferred category string
 */
export const inferCategoryFromPermissions = (permissions: ModulePermission[]): string => {
  // Check for global module - indicates Platform category
  const hasGlobal = permissions.some((m) => m.key === 'global' && m.canView)
  if (hasGlobal) return 'Platform'

  // Check for manage_agencies - indicates Platform or MSP category
  const hasManageAgencies = permissions.some((m) => m.key === 'manage_agencies' && m.canView)
  if (hasManageAgencies) return 'Platform'

  // Check for agency-specific modules
  const hasAgencyModules = permissions.some(
    (m) =>
      (m.key === 'agency_profile' || m.key === 'managed_sites' || m.key === 'agency_users_roles') &&
      m.canView,
  )
  if (hasAgencyModules) return 'Agency'

  // Check for company-specific modules
  const hasCompanyModules = permissions.some(
    (m) =>
      (m.key === 'company_profile' ||
        m.key === 'company_users_roles' ||
        m.key === 'company_manage_orders') &&
      m.canView,
  )
  if (hasCompanyModules) return 'Company'

  // Default to Platform for users with manage_companies
  const hasManageCompanies = permissions.some((m) => m.key === 'manage_companies' && m.canView)
  if (hasManageCompanies) return 'Platform'

  return 'Platform'
}
